<template>
  <div v-if="!!phaseData">
    <div v-if="!!predominantSymptom">
      <field-label
        label="As perguntas abaixo calculam os índices BASDAI, ASDAS PCR e VHS, EVA, DAS-28 e DAPSA."
        titleClass="text-h6"
        titleColorClass="grey--text text--darken-2"
      >
        <template #label-extra>
          <info-btn size="medium" class="ml-4" @click="showInfo" />
        </template>

        <div class="mt-4">
          <field-label v-for="question in indicatorQuestions" :key="question.key" :label="question.name" class="my-6">
            <v-slider
              v-model="phaseData[question.key]"
              color="primary"
              max="10"
              :tick-labels="sliderLabels"
              style="max-width: 800px"
              class="mt-3"
            />
          </field-label>
        </div>

        <field-label label="Proteína C reativa (PCR)">
          <div class="d-flex" :class="{ 'align-flex-start': $vuetify.breakpoint.smAndDown }">
            <text-field
              :class="{ 'mt-2': $vuetify.breakpoint.smAndDown }"
              :mask="['###']"
              v-model="phaseData['proteina_c_reativa'].value"
              :rules="[$validation.maxValue(300, true), $validation.minValue(0, true)]"
            />
            <radio-options
              class="ml-8 mt-0"
              :options="dosageOptions"
              v-model="phaseData['proteina_c_reativa'].dosageUnit"
              row
              hide-details
            />
          </div>
        </field-label>
        <field-label label="Velocidade de Hemossedimentação (VHS)">
          <div class="d-flex" :class="{ 'pt-2': $vuetify.breakpoint.smAndDown }">
            <text-field
              :mask="['###']"
              v-model="phaseData['velocidade_hemossedimentacao_vhs'].value"
              :rules="[$validation.maxValue(300, true), $validation.minValue(0, true)]"
            />
            <radio-options
              class="ml-8 mt-0"
              :options="vhsOptions"
              v-model="phaseData['velocidade_hemossedimentacao_vhs'].unit"
              row
              hide-details
            />
          </div>
        </field-label>

        <div class="my-6" style="max-width: 800px">
          <v-img :src="publicStorageUrl + '/disease-score-infographics/EVA-alta.png'" />
          <v-slider
            v-model="phaseData['escala_visual_analogica']"
            color="primary"
            max="10"
            :tick-labels="sliderLabels"
            class="mt-3"
          />
        </div>

        <field-label class="mt-8" label="Anotações">
          <text-area :readonly="disabled" :max-length="2000" v-model="phaseData.notes" />
        </field-label>
      </field-label>
    </div>
    <div v-else>
      <h2>É necessário definir um sintoma predominante na aba Anamnese &gt; Sintomas</h2>
    </div>

    <disease-activity-info-dialog ref="infoDialog" />
  </div>
</template>

<script>
import { createNamespacedHelpers } from 'vuex'
import { PUBLIC_STORAGE_URL } from '@/constants/global'
import FieldLabel from '@/components/FieldLabel.vue'
import InfoBtn from '@/components/InfoBtn.vue'
import BasePhase from '@/components/AttendanceForm/general/phase/BasePhase.vue'
import RadioOptions from '@/components/RadioOptions.vue'
import TextArea from '@/components/TextArea.vue'
import TextField from '@/components/TextField.vue'
import DiseaseActivityInfoDialog from '../DiseaseActivityInfoDialog.vue'

const { mapGetters: attendanceGettersMap } = createNamespacedHelpers('attendance')

export default {
  extends: BasePhase,
  components: { FieldLabel, InfoBtn, RadioOptions, TextArea, TextField, DiseaseActivityInfoDialog },
  computed: {
    ...attendanceGettersMap(['getPhaseDataByKey']),
    dosageOptions() {
      return [
        {
          text: 'mg/dl',
          value: 'MG_DL',
        },
        {
          text: 'mg/L',
          value: 'MG_L',
        },
      ]
    },
    vhsOptions() {
      return [
        {
          text: 'mm/H',
          value: 'MM_H',
        },
      ]
    },
    predominantSymptom() {
      return this.getPhaseDataByKey('sintomas')?.predominantSymptom
    },
    indicatorQuestions() {
      return this.predominantSymptom === 'AXIAL' ? this.axialIndicatorQuestions : this.perifericIndicatorQuestions
    },
  },
  data() {
    return {
      publicStorageUrl: PUBLIC_STORAGE_URL,
      sliderLabels: [...Array(11).keys()].map((i) => i.toString()),
      axialIndicatorQuestions: [
        { key: 'nivel_fadiga', name: 'Como o paciente descreve o nível de fadiga ou cansaço?' },
        {
          key: 'nivel_dor_coluna',
          name: 'Como o paciente descreve o nível de dor em coluna cervical, lombar e sacroilíaca?',
        },
        {
          key: 'nivel_dor_articulacoes',
          name: 'Como o paciente descreve o nível de dor ou edema de articulações que não a coluna cervical, lombar e sacroilíaca?',
        },
        {
          key: 'nivel_desconforto_areas_dolorosas',
          name: 'Como o paciente descreve o nível de desconforto em áreas dolorosas?',
        },
        {
          key: 'nivel_desconforto_matinal',
          name: 'Como o paciente descreve o nível de desconforto ao levantar pela manhã?',
        },
        {
          key: 'tempo_rigidez_matinal',
          name: 'Quanto tempo dura a rigidez matinal pela manhã? Importante: sendo que 1h é igual a 5 e 2h é igual a 10.',
        },
        { key: 'escala_global', name: 'Escala global do paciente? (avaliação do médico)' },
      ],
      perifericIndicatorQuestions: [
        {
          key: 'nivel_atividade_doenca_reumatologica_ultima_semana',
          name: 'Quão ativa foi sua doença reumatológica na última semana?',
        },
        {
          key: 'nivel_dor_articular_ultima_semana',
          name: 'Como foi seu nível de dor articular na última semana?',
        },
      ],
    }
  },
  mounted() {
    if (this.predominantSymptom !== this.phaseData.predominantSymptom) {
      this.phaseData = this.getDefaultData()
      this.phaseData.predominantSymptom = this.predominantSymptom
    }
  },
  methods: {
    getDefaultData() {
      const data = {
        predominantSymptom: null,
        proteina_c_reativa: {
          value: null,
          dosageUnit: null,
        },
        velocidade_hemossedimentacao_vhs: {
          value: null,
          unit: 'MM_H',
        },
      }
      return data
    },
    showInfo() {
      this.$refs.infoDialog.openDialog()
    },
  },
}
</script>

<style></style>
